/**
 * RealtimeManager — multiplexador de canais Supabase Realtime.
 *
 * Problema: cada componente abria seu próprio `supabase.channel()`. Na Home
 * coexistiam ~6 canais por usuário (presence + 2 overlays + 3 cards). O limite
 * de canais/conexões simultâneos do projeto Supabase é finito → sob alto fluxo,
 * canais começam a falhar silenciosamente. Ver auditoria, item #6.
 *
 * Solução: assinantes registram seus bindings de `postgres_changes` em um GRUPO
 * nomeado; o manager consolida todos os bindings do grupo em UM único canal
 * (refcount). Quando o último assinante sai, o canal é derrubado.
 *
 * Por que grupos nomeados (e não um único canal global):
 *   - As bordas têm ciclos de vida distintos. Overlays de convite são always-on
 *     (montam uma vez em App.js); cards da Home montam/desmontam com a Home.
 *   - Um canal global seria reconstruído a cada navegação Home↔fora, e o
 *     Supabase NÃO reenvia eventos ocorridos durante a janela de reconstrução
 *     → convites perdidos. Grupos com membership estável reconstroem zero vez
 *     no meio da sessão.
 *
 * Padrões: Singleton + Pub/Sub interno + refcount. Debounce coalesce o burst de
 * registros de um mount em UMA construção de canal.
 *
 * Resiliência (#4.3): NÃO reimplementamos backoff — o supabase-js já reconecta
 * canais automaticamente. Apenas observamos o status e reportamos degradação à
 * telemetria; consumidores críticos mantêm seu próprio backstop poll.
 */

import { supabase } from '../lib/supabase';
import { captureException, captureMessage } from './telemetry';

const REBUILD_DEBOUNCE_MS = 60;

// Degradação: só reporta ao Sentry após N erros consecutivos no mesmo canal,
// com cooldown para evitar flood. Reconexões transientes (tenant lifecycle do
// Supabase — desliga sem usuários, reinicia ao conectar) são ignoradas.
const DEGRADE_THRESHOLD = 3;      // erros consecutivos antes de reportar
const DEGRADE_COOLDOWN_MS = 60000; // 60s entre reports do mesmo canal
const degradeState = new Map();    // name -> { count, lastReportedAt }

// name -> { channel, subscribers: Map<token, bindingsRef>, timer, status }
const groups = new Map();
let tokenSeq = 0;

const ensureGroup = (name) => {
    let group = groups.get(name);
    if (!group) {
        group = { channel: null, subscribers: new Map(), timer: null, status: 'idle' };
        groups.set(name, group);
    }
    return group;
};

const teardownChannel = (group) => {
    if (group.channel) {
        try {
            supabase.removeChannel(group.channel);
        } catch {
            // remoção é best-effort; canal pode já estar fechado.
        }
        group.channel = null;
    }
};

const rebuild = (name) => {
    const group = groups.get(name);
    if (!group) return;

    teardownChannel(group);

    if (group.subscribers.size === 0) {
        groups.delete(name);
        return;
    }

    let channel = supabase.channel(name);
    let bindingCount = 0;

    for (const bindingsRef of group.subscribers.values()) {
        const bindings = bindingsRef.current || [];
        bindings.forEach((binding, index) => {
            const { event, table, filter, schema } = binding;
            if (!event || !table) return;

            const config = { event, schema: schema || 'public', table };
            if (filter) config.filter = filter;
            bindingCount += 1;

            channel = channel.on('postgres_changes', config, (payload) => {
                if (__DEV__) {
                    console.log(`[realtime] evento em "${name}": ${payload?.eventType} ${table}`);
                }
                // Lê o handler ATUAL via ref: a identidade do handler pode mudar
                // entre renders sem exigir reconstrução do canal (só descritores
                // — event/table/filter — disparam rebuild).
                const current = bindingsRef.current?.[index]?.handler;
                try {
                    current?.(payload);
                } catch (error) {
                    captureException(error, { scope: 'realtime', channel: name, table });
                }
            });
        });
    }

    channel.subscribe((status) => {
        group.status = status;
        if (__DEV__) {
            console.log(`[realtime] canal "${name}" → ${status} (${bindingCount} bindings)`);
        }
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            // Debounce: só reporta degradação sustentada (≥3 erros consecutivos
            // no mesmo canal, com cooldown de 60s). Reconexões transitórias do
            // tenant lifecycle do Supabase não poluem mais a quota do Sentry.
            const state = degradeState.get(name) || { count: 0, lastReportedAt: 0 };
            state.count += 1;
            const now = Date.now();
            if (state.count >= DEGRADE_THRESHOLD && now - state.lastReportedAt > DEGRADE_COOLDOWN_MS) {
                captureMessage('Realtime channel degraded', {
                    channel: name, status, consecutiveErrors: state.count,
                });
                state.lastReportedAt = now;
            }
            degradeState.set(name, state);
        } else if (status === 'SUBSCRIBED') {
            // Canal recuperou — reseta o contador de erros.
            degradeState.delete(name);
        }
    });

    group.channel = channel;
};

const scheduleRebuild = (name) => {
    const group = groups.get(name);
    if (!group) return;
    if (group.timer) clearTimeout(group.timer);
    group.timer = setTimeout(() => {
        group.timer = null;
        rebuild(name);
    }, REBUILD_DEBOUNCE_MS);
};

/**
 * Registra um assinante no grupo. `bindingsRef` é uma ref cujo `.current` é um
 * array de bindings { event, table, filter?, schema?, handler }. Retorna um
 * token opaco para `unregisterRealtime`.
 */
export const registerRealtime = (groupName, bindingsRef) => {
    const group = ensureGroup(groupName);
    const token = ++tokenSeq;
    group.subscribers.set(token, bindingsRef);
    scheduleRebuild(groupName);
    return token;
};

export const unregisterRealtime = (groupName, token) => {
    const group = groups.get(groupName);
    if (!group) return;
    group.subscribers.delete(token);
    scheduleRebuild(groupName);
};

export default { registerRealtime, unregisterRealtime };
